import { without } from 'lodash'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import Tappable from 'react-tappable'
import SuggestedTagWrapper from 'shared/components/suggested-tags'
import TaggingTips from 'shared/components/suggested-tags/tagging-tips'
import Toggle from 'shared/components/toggle/toggle'
// @ts-ignore
import { giphyDarkCharcoal } from 'shared/css/colors.css'
import { STATUS } from 'shared/redux/creation-tool'
import { message, SendMessageAction } from 'shared/redux/message'
import { getCreationStatus } from 'shared/redux/reducers'
import { encodeGAParams, googleAnalyticsEvent } from 'shared/util/analytics'
import { TAG_LIMIT } from 'shared/variables'
// @ts-ignore
import css from '../info-fields.css'

type Props = {
    tags: string[]
    featuredTags: string[]
    onChange: (data: object) => void
    status: string
    isPrivate: boolean
    showPrivacy: boolean
    allowFeaturedTags: boolean
    allowMoreTags: boolean
    showSuggestedTags: boolean
    endpoint: string
    gifIds: string[]
    userName: string
}

type ReduxProps = {
    message: SendMessageAction
}

const PrivacyAndTags = ({
    tags,
    featuredTags,
    onChange,
    message,
    status,
    showPrivacy,
    isPrivate,
    allowFeaturedTags,
    allowMoreTags,
    showSuggestedTags,
    endpoint,
    gifIds,
    userName,
}: Props & ReduxProps) => {
    const [currentTag, setCurrentTag] = useState<string>('')
    const [lastEnteredTags, setLastEnteredTags] = useState<string[]>([])
    const [manualTags, setManualTags] = useState<string[]>([])

    useEffect(() => {
        if (status === STATUS.WILL_UPLOAD) {
            addTags()
        }
        if (status === STATUS.UPLOADING) {
            fireTagEvents()
        }
    }, [status])

    const addTags = () => {
        const newTags = currentTag
            .split(',')
            .map(tag => tag.trim().replace('#', ''))
            .filter(tag => tag && tags.indexOf(tag) < 0)
        setCurrentTag('')
        setLastEnteredTags(newTags)
        setManualTags([...manualTags, ...newTags])

        if (newTags.length + tags.length > TAG_LIMIT && !allowMoreTags) {
            message('Sorry! Max number of tags reached.', 'error')
            return
        }
        onChange({ tags: [...tags, ...newTags] })
    }

    const fireTagEvents = () => {
        manualTags.forEach(tag => {
            googleAnalyticsEvent({
                category: endpoint,
                action: 'Tag Added',
                label: encodeGAParams({
                    gif_id: '', // gif_id will not exist yet in upload/create flow
                    tag_name: tag,
                    user_name: userName,
                }),
            })
        })
    }

    const addSuggestedTag = (tag: string) => {
        if (tags.length + 1 > TAG_LIMIT && !allowMoreTags) {
            message('Sorry! Max number of tags reached.', 'error')
            return
        }

        const newTags = [tag].map(tag => tag.trim().replace('#', '')).filter(tag => tag && tags.indexOf(tag) < 0)

        onChange({ tags: [...tags, ...newTags] })
    }

    const removeTag = (tag: string) => {
        setManualTags(without(manualTags, tag))
        onChange({
            tags: without(tags, tag),
            featuredTags: without(featuredTags, tag),
        })
    }

    const toggleFeatureTag = (tag: string) => {
        const featuredTagIndex = featuredTags.indexOf(tag)
        const newFeaturedTags = [...featuredTags]
        if (featuredTagIndex > -1) {
            newFeaturedTags.splice(featuredTagIndex, 1)
        } else {
            newFeaturedTags.push(tag)
        }
        onChange({ featuredTags: newFeaturedTags })
    }

    const onTagKeyDown = ({ which }) => {
        if (which === 13) {
            addTags()
        }
    }

    return (
        <div>
            <div className={css.field}>
                <div className={css.flexField}>
                    <div className={css.tags}>
                        <div className={css.labelAndTipsWrapper}>
                            <label className={css.addTags}>Add tags</label>
                            <TaggingTips isVertical={false} />
                        </div>
                        <div className={css.tagField}>
                            <input
                                type="text"
                                value={currentTag}
                                placeholder="Enter tags"
                                onKeyDown={e => onTagKeyDown(e)}
                                onChange={e => setCurrentTag(e.target.value)}
                            />
                            <Tappable className={css.tagAdd} onTap={() => addTags()} />
                        </div>
                    </div>
                    {showPrivacy && (
                        <div className={css.privacy}>
                            <label className={css.label}>Public</label>
                            <Toggle
                                isOn={!isPrivate}
                                height={40}
                                width={80}
                                onElement={<span>On</span>}
                                offElement={<span>Off</span>}
                                backgroundColor={giphyDarkCharcoal}
                                toggle={isPrivate => onChange({ isPrivate })}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={css.field}>
                {tags.map(tag => (
                    <Tappable
                        key={tag}
                        className={`${css.tag} ${featuredTags.includes(tag) ? css.featured : ''} ${allowFeaturedTags ? css.featurable : ''
                            }`}
                        onTap={() => allowFeaturedTags && toggleFeatureTag(tag)}
                    >
                        <span className={css.tagLabel}>{tag}</span>
                        <Tappable className={css.tagRemove} onTap={() => removeTag(tag)} />
                    </Tappable>
                ))}
            </div>
            {showSuggestedTags && (
                <SuggestedTagWrapper
                    currentTags={tags}
                    lastEnteredTags={lastEnteredTags}
                    endpoint={endpoint}
                    gifIds={gifIds}
                    addSugTag={addSuggestedTag}
                    uploadStatus={status}
                    isVertical={false}
                    userName={userName}
                />
            )}
        </div>
    )
}

export default connect(state => ({ status: getCreationStatus(state) }), { message })(PrivacyAndTags)
